import React, { useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import useScrollToTop from "../../blocks/logic/usScrollToTop";
import SEO from "../../components/SEO";
import ShopifyBuyButton from "../../components/ShopifyBuyButton";
import { Container, Section } from "../../components/ui";

const WALL_MAP_PRODUCT_ID = "10239741395256";

const ProductSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ImageContainer = styled.div`
  position: sticky;
  top: 100px;
  
  @media (max-width: 968px) {
    position: static;
  }
`;

const ImageGallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MainImage = styled.div`
  width: 100%;
  aspect-ratio: 4/3;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1);
  cursor: pointer;
  transition: opacity 150ms ease;
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const Thumbnail = styled.div`
  width: 80px;
  height: 60px;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  border-radius: 0.5rem;
  cursor: pointer;
  border: 2px solid ${({ $active }) => $active ? '#eab308' : 'transparent'};
  transition: all 150ms ease;
  
  &:hover {
    border-color: #eab308;
  }
`;

const ProductInfo = styled.div``;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 1rem;
  
  a {
    color: #6b7280;
    &:hover { color: #0f0f0f; }
  }
`;

const Badge = styled.span`
  display: inline-block;
  background: #0f0f0f;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

const ProductTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const PriceTag = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #eab308;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  color: #6b7280;
  font-size: 1.125rem;
  line-height: 1.75;
  margin-bottom: 2rem;
`;

const Specs = styled.div`
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 0.75rem;
  margin-bottom: 2rem;
`;

const SpecGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const SpecItem = styled.div``;

const SpecLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const SpecValue = styled.div`
  font-weight: 600;
  color: #0f0f0f;
`;

const BuySection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
`;

const Features = styled.div`
  margin-top: 2rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 1rem;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: #374151;
  
  &::before {
    content: "✓";
    color: #22c55e;
    font-weight: 700;
  }
`;

const DetailsSection = styled(Section)`
  background: #f9fafb;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.div`
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 1rem;
`;

const DetailIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const DetailTitle = styled.h4`
  margin-bottom: 0.5rem;
`;

const DetailText = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
`;

const images = [
  "https://res.cloudinary.com/dqy3fktlv/image/upload/v1734522897/IMG_7101copycopy_z9ck6d.jpg",
  "https://res.cloudinary.com/dqy3fktlv/image/upload/v1734542869/IMG_7077crop_lghhnb.jpg",
];

const WhiteboardProduct = () => {
  useScrollToTop();
  const [activeImage, setActiveImage] = useState(0);

  return (
    <>
      <SEO
        title="New Calendar Wall Map"
        description="3'x4' dry erase wall map for The New Calendar. Plan your entire year with this large-format perpetual calendar."
        url="https://thenewcalendar.com/newcalendar_wallmap"
        image={images[0]}
        type="product"
      />

      <Section>
        <Container>
          <ProductSection>
            <ImageContainer>
              <ImageGallery>
                <MainImage $src={images[activeImage]} />
                <ThumbnailRow>
                  {images.map((img, i) => (
                    <Thumbnail 
                      key={i}
                      $src={img}
                      $active={activeImage === i}
                      onClick={() => setActiveImage(i)}
                    />
                  ))}
                </ThumbnailRow>
              </ImageGallery>
            </ImageContainer>

            <ProductInfo>
              <Breadcrumb>
                <Link to="/shop">Shop</Link> / Wall Map
              </Breadcrumb>

              <Badge>Premium</Badge>
              <ProductTitle>New Calendar Wall Map</ProductTitle>
              <PriceTag>$150</PriceTag>

              <Description>
                The ultimate planning tool for serious goal-setters. This large-format 
                dry-erase wall map lets you visualize your entire year at a glance using 
                The New Calendar's revolutionary 5-season system.
              </Description>

              <Specs>
                <SpecGrid>
                  <SpecItem>
                    <SpecLabel>Dimensions</SpecLabel>
                    <SpecValue>3' × 4' (36" × 48")</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Material</SpecLabel>
                    <SpecValue>Dry-Erase Laminate</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Format</SpecLabel>
                    <SpecValue>5-Season Layout</SpecValue>
                  </SpecItem>
                  <SpecItem>
                    <SpecLabel>Reusable</SpecLabel>
                    <SpecValue>Yes, perpetual design</SpecValue>
                  </SpecItem>
                </SpecGrid>
              </Specs>

              <BuySection>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <ShopifyBuyButton 
                    productId={WALL_MAP_PRODUCT_ID} 
                    buttonText="Add to Cart"
                    destination="cart"
                  />
                  <ShopifyBuyButton 
                    productId={WALL_MAP_PRODUCT_ID} 
                    buttonText="Buy Now - $150"
                    destination="checkout"
                    showOptions={false}
                  />
                </div>
              </BuySection>

              <Features>
                <FeatureTitle>Perfect For</FeatureTitle>
                <FeatureList>
                  <FeatureItem>Annual goal setting and tracking</FeatureItem>
                  <FeatureItem>Team planning in offices</FeatureItem>
                  <FeatureItem>Classroom scheduling</FeatureItem>
                  <FeatureItem>Project timelines and milestones</FeatureItem>
                  <FeatureItem>Seasonal business planning</FeatureItem>
                </FeatureList>
              </Features>
            </ProductInfo>
          </ProductSection>
        </Container>
      </Section>

      <DetailsSection>
        <Container>
          <h2 style={{ textAlign: 'center', marginBottom: '2rem' }}>Why Go Large Format?</h2>
          <DetailGrid>
            <DetailCard>
              <DetailIcon>👁️</DetailIcon>
              <DetailTitle>See Everything</DetailTitle>
              <DetailText>Visualize your entire year at a glance. No scrolling, no flipping pages.</DetailText>
            </DetailCard>
            <DetailCard>
              <DetailIcon>✏️</DetailIcon>
              <DetailTitle>Write & Erase</DetailTitle>
              <DetailText>Dry-erase surface lets you update plans as they evolve.</DetailText>
            </DetailCard>
            <DetailCard>
              <DetailIcon>🎯</DetailIcon>
              <DetailTitle>Stay Focused</DetailTitle>
              <DetailText>Keep your goals front and center. Out of sight, out of mind doesn't apply here.</DetailText>
            </DetailCard>
          </DetailGrid>
        </Container>
      </DetailsSection>
    </>
  );
};

export default WhiteboardProduct;
